import {createSlice, isAnyOf, PayloadAction} from '@reduxjs/toolkit';

import {extendedAPI} from './api';
import {extractTaskInstanceId, myTasksFlattened} from './utils';

import type {TaskDashboardLocationParamsType} from './types';
import type {
  SlimTaskInstanceSerializedType,
  TaskConcreteInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

export interface TasksState {
  instancesById: {
    [id: string]:
      | TaskInstanceSerializedType
      | TaskConcreteInstanceSerializedType;
  };
  slimInstancesById: {[id: string]: SlimTaskInstanceSerializedType};
  dashboard: {
    lastUsed: 'mine' | 'all'; // 'all' or 'mine'
    allTasksFilters: TaskDashboardLocationParamsType;
    mineCollapsed: {
      overdue: {root: boolean; toYou: boolean; fromYou: boolean};
      today: {root: boolean; toYou: boolean; fromYou: boolean};
      soon: {root: boolean; toYou: boolean; fromYou: boolean};
    };
  };
}

const initialState: TasksState = {
  instancesById: {},
  slimInstancesById: {},
  dashboard: {
    lastUsed: 'mine',
    allTasksFilters: {},
    mineCollapsed: {
      overdue: {root: false, toYou: false, fromYou: false},
      today: {root: false, toYou: false, fromYou: false},
      soon: {root: false, toYou: false, fromYou: false},
    },
  },
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setAllTasksFilters: (
      state,
      {payload}: PayloadAction<TaskDashboardLocationParamsType>
    ) => {
      state.dashboard.allTasksFilters = payload;
    },
    setLastUsedTasksDashboard: (
      state,
      {payload}: PayloadAction<TasksState['dashboard']['lastUsed']>
    ) => {
      state.dashboard.lastUsed = payload;
    },
    toggleMyTasksCollapsed: (
      state,
      {
        payload: [section, subsection],
      }: PayloadAction<
        [
          section: keyof TasksState['dashboard']['mineCollapsed'],
          subsection: keyof TasksState['dashboard']['mineCollapsed']['today']
        ]
      >
    ) => {
      state.dashboard.mineCollapsed[section][subsection] =
        !state.dashboard.mineCollapsed[section][subsection];
    },
  },
  extraReducers: (builder) =>
    builder
      .addMatcher(
        extendedAPI.endpoints.getSlimTasks.matchFulfilled,
        (state, {payload: {results}}) => {
          // save slim-tasks by id so we can use them to show some metadata
          // in the detail page while the fat task is fetched
          for (const task of results) {
            state.slimInstancesById[extractTaskInstanceId(task)] = task;
          }
        }
      )
      .addMatcher(
        extendedAPI.endpoints.getMyTasks.matchFulfilled,
        (state, {payload: response}) => {
          myTasksFlattened(response).forEach((t) => {
            state.slimInstancesById[extractTaskInstanceId(t)] = t;
          });
        }
      )
      .addMatcher(
        isAnyOf(
          extendedAPI.endpoints.updateTaskInstance.matchFulfilled,
          extendedAPI.endpoints.getTask.matchFulfilled
        ),
        (state, {payload: task}) => {
          const taskId = extractTaskInstanceId(task);

          if (
            task.id != null &&
            state.slimInstancesById[task.id.toString()] != null
          ) {
            // here we cheat and update the slim instances with the fat instances
            // data. This is useful to quickly update interface using slim instances
            // when we update a fat instance (mark slim instance as completed)
            state.slimInstancesById[task.id] = updateSlimTaskWithFatData(
              state.slimInstancesById[task.id],
              task
            );
          }
          if (
            (task.id == null || taskId != task.id.toString()) &&
            state.slimInstancesById[taskId] != null
          ) {
            // update ghost instances as well
            state.slimInstancesById[taskId] = updateSlimTaskWithFatData(
              state.slimInstancesById[taskId],
              task
            );
          }

          state.instancesById[taskId] = task;
        }
      ),
});

export const {
  setAllTasksFilters,
  setLastUsedTasksDashboard,
  toggleMyTasksCollapsed,
} = tasksSlice.actions;

export const tasksReducer = tasksSlice.reducer;

/**
 * Copy user-editable fields from a fat task instance to a slim task instance
 *
 * @param slim slim task to be updated
 * @param param1 fat task with updated fields
 * @returns updated slim task
 */
function updateSlimTaskWithFatData(
  slim: SlimTaskInstanceSerializedType,
  {
    completed,
    started_at,
    is_completeable,
    progress_percent,
    first_step_completed_at,
    progress,
  }: TaskInstanceSerializedType
): SlimTaskInstanceSerializedType {
  return {
    ...slim,
    completed,
    started_at,
    is_completeable,
    progress_percent,
    first_step_completed_at,
    progress,
  };
}
